import { Box, Stack, Typography } from '@mui/material';
import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Team } from '../../types/domain';
import { useAutoRotate } from '../../hooks/useAutoRotate';
import { MEDAL_COLORS, buildBoardRows, type BoardRow } from './BoardShared';
import { buildBoardSlots } from './boardSlots';

interface CarouselBoardProps {
  competition: CompetitionPublic;
  results: Result[];
  overall: OverallRanking[];
  allTeams: Team[] | undefined;
}

/** Fullscreen jedna disciplína / overall, rotuje. Styl scoreboard, velký font. */
export const CarouselBoard = ({ competition, results, overall, allTeams }: CarouselBoardProps) => {
  const slots = buildBoardSlots(competition, results, overall);
  const { index, setIndex } = useAutoRotate({
    total: slots.length,
    intervalMs: Math.max(5, competition.publicRotationSeconds ?? 10) * 1000,
  });
  const active = slots[index];

  if (!active) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
        <Typography variant="h5">Čekáme na první výsledky…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, letterSpacing: '0.15em', color: 'primary.main' }}
        >
          {active.category.name}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 40, md: 64 },
            fontWeight: 900,
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'text.primary',
          }}
        >
          {active.kind === 'DISCIPLINE' ? active.discipline.name : 'Celkové pořadí'}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {active.kind === 'DISCIPLINE' ? (
          <DisciplineRows
            results={active.results}
            categoryId={active.category.id}
            disciplineId={active.discipline.id}
            allTeams={allTeams}
            showAll={!!competition.boardShowAllTeams}
            sortMode={competition.boardSortMode}
          />
        ) : (
          <OverallRows ranking={active.ranking} />
        )}
      </Box>

      {slots.length > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          {slots.map((s, i) => (
            <Box
              key={s.key}
              onClick={() => setIndex(i)}
              sx={{
                width: i === index ? 32 : 10,
                height: 8,
                borderRadius: 4,
                bgcolor: i === index ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 200ms ease',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const DisciplineRows = ({
  results,
  categoryId,
  disciplineId,
  allTeams,
  showAll,
  sortMode,
}: {
  results: Result[];
  categoryId: number;
  disciplineId: number;
  allTeams: Team[] | undefined;
  showAll: boolean;
  sortMode: CompetitionPublic['boardSortMode'];
}) => {
  const rows: BoardRow[] = buildBoardRows(results, allTeams, categoryId, disciplineId, {
    showAll,
    sortMode,
    topN: 10,
  });
  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: { xs: 20, md: 32 }, color: 'text.secondary' }}>
        Zatím žádné výsledky
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {rows.map((r) => {
        const medal = r.rank != null ? MEDAL_COLORS[r.rank] : null;
        return (
          <Box
            key={r.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(60px, 80px) 1fr auto',
              alignItems: 'center',
              gap: { xs: 1.5, md: 3 },
              py: { xs: 0.5, md: 1 },
              px: { xs: 1, md: 2 },
              bgcolor: medal ? `${medal.bg}33` : 'transparent',
              borderRadius: 2,
              borderLeft: medal ? `6px solid ${medal.bg}` : '6px solid transparent',
              opacity: r.__missing ? 0.55 : 1,
            }}
          >
            <RankBadgeCarousel rank={r.rank} medal={medal} />
            <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontFamily: 'monospace', color: 'text.secondary' }}>
              #{r.teamStartNumber}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 24, md: 40 },
                fontWeight: 700,
                textTransform: 'uppercase',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
            >
              {r.teamName}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 28, md: 48 },
                fontFamily: 'monospace',
                fontWeight: 800,
                color: medal ? medal.bg : 'text.primary',
                textAlign: 'right',
              }}
            >
              {r.isNp ? 'NP' : r.timeFinalFormatted}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const RankBadgeCarousel = ({ rank, medal }: { rank: number | null; medal: { bg: string; fg: string } | null }) => {
  const bg = medal ? medal.bg : 'rgba(255,255,255,0.15)';
  const fg = medal ? medal.fg : 'inherit';
  const label = rank ?? '–';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: 56, md: 88 },
        height: { xs: 56, md: 88 },
        borderRadius: '999px',
        bgcolor: bg,
        color: fg,
        fontWeight: 900,
        fontSize: { xs: 28, md: 48 },
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
};

const OverallRows = ({ ranking }: { ranking: OverallRanking }) => {
  const top = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank).slice(0, 10);
  return (
    <Stack spacing={1}>
      {top.map((t) => {
        const medal = MEDAL_COLORS[t.finalRank];
        return (
          <Box
            key={t.teamId}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(70px, 90px) minmax(60px, 80px) 1fr auto',
              alignItems: 'center',
              gap: { xs: 1.5, md: 3 },
              py: { xs: 0.5, md: 1 },
              px: { xs: 1, md: 2 },
              bgcolor: medal ? `${medal.bg}33` : 'transparent',
              borderRadius: 2,
              borderLeft: medal ? `6px solid ${medal.bg}` : '6px solid transparent',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 32, md: 56 },
                fontWeight: 900,
                color: medal ? medal.bg : 'text.primary',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t.finalRank}
            </Typography>
            <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontFamily: 'monospace', color: 'text.secondary' }}>
              #{t.startNumber}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 24, md: 40 },
                fontWeight: 700,
                textTransform: 'uppercase',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
            >
              {t.teamName}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 28, md: 48 },
                fontFamily: 'monospace',
                fontWeight: 800,
                color: medal ? medal.bg : 'text.primary',
                textAlign: 'right',
              }}
            >
              {t.totalPoints} b.
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};
